import React, {
  useState,
  useEffect,
  useMemo,
  createContext,
  useContext
} from "react"
import { useParams } from "react-router-dom"

import SeasonSelector from "../../components/SeasonSelector"
import { AirportSeasonConfigurations } from "../../api/AirportSeasonConfiguration"
import {
  beginTransaction,
  endTransaction
} from "../../containers/transactionSlice"

import Button from "../../components/generic/buttons/Button"
import { ResponsePane } from "../../components/ResponsePane"
import { Flight } from "../../api/Flight"
import { v4 as uuid } from "uuid"

import {
  IUserSettingsSelection,
  updateAirportSeasonsSelection,
  updateUserSettingsSelection
} from "../../containers/selectionSlice"
import { getTimeAndCodeFilteredAirportSeasons } from "../../components/Utils"
import _ from "lodash"
import TransactionItem from "../../components/transaction/TransactionItem"
import { Transaction, Transactions } from "../../api/Transaction"
import FlightAvailabilityPanel from "../../components/FlightAvailabilityPanel"
import * as Utils from "../../components/Utils"
import { useForm } from "react-hook-form"
import SwitchBtn from "../../components/generic/form/SwitchBtn"
import { getFlightSelectorConfiguration } from "../../components/Utils"
import {
  FlightMode,
  EditMode,
  GCRMode,
  PageGroup,
  handleHasFlightsWithTerminal,
  handleHasFlightsWithParkingLoc,
  handleHasFlightsWithServiceTypeAcReg
} from "../../common/transaction"
import {
  deleteAllTransactions,
  getAirportSeasonConfigurations
} from "../../api/BackendCalls"

import { getPageConfig } from "../Config"
import { CSSTransition, TransitionGroup } from "react-transition-group"
import { handleGetTransactionPageGroup, PageId } from "../../common/pageId"
import { CodeType } from "../../components/AirportSelector"

import { dateRangeIsValid } from "../../components/PageConfigUtils"

import {
  BtnText,
  FieldName,
  FlightText,
  FormText,
  LoadingText,
  userSettingsKey
} from "../../common/text"
import useInputAlert from "../../hooks/useInputAlert"
import { timeOptions } from "../pageUtils"
import AddFlightBtn from "./AddFlightBtn"
import { AppContext } from "../../app/App"
import { handleGetFlightAvailability } from "../Support"
import { PageTemplate } from "../../components/pageTemplate/PageTemplate"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import {
  showFieldSection,
  showOperatorField
} from "../../components/transaction/Config"
import { CommonErrorText } from "../../common/errorText"
import { Season, Seasons } from "../../api/Seasons"

interface Props {
  pageId: PageId
  date?: string
  time?: string
  adt?: FlightMode
}
export const GabaContext = createContext(null)
export const AddContext = createContext(null)

export const AddFlights: React.FunctionComponent<Props> = ({ pageId }) => {
  const { season, useLocalTime, airports } = useAppSelector(
    (state) => state["selectionReducer"].airportSeasonsSelector
  )
  const { versionInfo } = useAppSelector((state) => state["appReducer"])
  const { adt, time, date } = useParams<Props>()
  const { showOptionalFields }: IUserSettingsSelection = useAppSelector(
    (state) => state["selectionReducer"].userSettings
  )
  const appContext = useContext(AppContext)

  const dispatchFn = () => {
    dispatch(
      updateAirportSeasonsSelection({
        field: FieldName.useLocalTime,
        value: local
      })
    )
  }

  const seasons: Seasons = useAppSelector(
    (state) => state["appReducer"].seasons
  )

  const [airportSeasons, setAirportSeasons] =
    useState<AirportSeasonConfigurations>([])
  const [handleAsBlock, setHandleAsBlock] = useState(false)
  const [transactions, setTransactions] = useState<Transactions>([])
  const hasActiveCards = transactions.length === 0 ? false : true
  const {
    handleTimeChange,
    handleCloseModal,
    handleResetFlights,
    local,
    showModal,
    selectedTime
  } = useInputAlert(hasActiveCards, dispatchFn, useLocalTime)

  const [arrMatrixAsHtml, setArrMatrixAsHtml] = useState(null)
  const [depMatrixAsHtml, setDepMatrixAsHtml] = useState(null)
  const [checkAvailabilityTransIdx, setCheckAvailabilityTransIdx] =
    useState(null)
  const { handleSubmit } = useForm()
  const [transactionInFocus, setTransactionInFocus] = useState(0)
  const [indicatorClicked, setIndicatorClicked] = useState<boolean>(false)

  const dispatch = useAppDispatch()

  const pageConfig = getPageConfig[pageId]
  const pageTitle = pageConfig.pageTitle
  const codeType = pageConfig.codeType
  const transactionPageGroup = handleGetTransactionPageGroup(pageId)
  const isGabaPage = transactionPageGroup === PageGroup.GABA
  const isHandlingPage = transactionPageGroup === PageGroup.HANDLING

  useEffect(() => {
    deleteAllTransactions(pageId)
    appContext.handleSetIsLoading({
      isLoading: true,
      loadingTxt: LoadingText.loadingAirportSeasons
    })
    getAirportSeasonConfigurations(
      pageId,
      (airportSeasons) => {
        setAirportSeasons(airportSeasons)
        appContext.handleSetIsLoading({ isLoading: false })
      },
      () =>
        appContext.handleSetIsLoading({ isLoading: false, fromFailure: true })
    )

    return () => {
      //cleanup on unmount
      dispatch(endTransaction({}))
    }
    // eslint-disable-next-line
  }, [pageId])

  const [touched, setTouched] = useState<number | "ALL">(null)
  const [invalidFieldsArr, setInvalidFieldsArr] = useState([])

  useEffect(() => {
    const b = invalidFieldsArr.filter((a) => a.id !== 0)
    setInvalidFieldsArr(b)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const startTransaction = () => {
    setTouched("ALL")
    const invalidFields = invalidFieldsArr.map((a) => a.arr)
    console.log(invalidFields)

    if (error) {
      dispatch(
        updateUserSettingsSelection({
          field: userSettingsKey.showOptionalFields,
          value: true
        })
      )
    }

    if (invalidFields.flat(1).length === 0 && !dateHasError && !error) {
      dispatch(beginTransaction({}))
    }
  }

  const airportList = useMemo(() => {
    const useICAOCode = codeType === CodeType.ICAO

    const timeAirportSeasons = getTimeAndCodeFilteredAirportSeasons(
      useLocalTime,
      useICAOCode,
      airportSeasons
    )

    const timeAirportSeasonsFiltered = timeAirportSeasons.filter(
      (as) => as.season === season
    )

    const airports = timeAirportSeasonsFiltered.map((as) => as.airport)
    return airports
  }, [airportSeasons, season, useLocalTime, codeType])

  const handleGetRegAirports = () => {
    return airportList.filter((airport) => {
      const t = airportSeasons.find(
        (as) => as.season === season && as.airport === airport
      )
      return t ? t.allowGcrRegMode : false
    })
  }

  const [hideAcReg, setHideAcReg] = useState(true)

  useEffect(() => {
    if (airportList.length && adt && date && time && !transactions.length) {
      handleAddFlight(adt)
    }

    setHideAcReg(handleGetRegAirports().length === 0)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [airportList, airportSeasons, pageId, season, hideAcReg])

  const handleAutoFill = (fromFlight: Flight, toFlight: Flight): Flight => {
    // period
    toFlight.startDate = fromFlight.startDate
    toFlight.endDate = fromFlight.endDate
    toFlight.doop = fromFlight.doop
    toFlight.woop = fromFlight.woop
    // aircraft
    toFlight.aircraftType = fromFlight.aircraftType
    toFlight.aircraftRegistration = fromFlight.aircraftRegistration
    toFlight.numSeats = fromFlight.numSeats
    // operator
    toFlight.operatorArr = fromFlight.operatorDep // dep -> arr (arr -> dep should be a turnaround)
    if (toFlight.flightMode === FlightMode.Turnaround) {
      toFlight.operatorDep = fromFlight.operatorDep // dep = arr for turnaround
    }
    // airport - default
    toFlight.airport = fromFlight.airport

    // Auxiliary functions
    const iata2icao = (airportIata: string) => {
      const airSea = airportSeasons.find((as) => as.airport === airportIata)
      return airSea ? airSea.airportIcao : ""
    }
    const icao2iata = (airportIcao: string) => {
      const airSea = airportSeasons.find((as) => as.airportIcao === airportIcao)
      return airSea ? airSea.airport : ""
    }

    // is next/dest stn selectable in airport dropdown
    const fromFlightAirportExist = airportList.find((airportIata) => {
      // next, dest follow codeType
      const airport =
        codeType === CodeType.ICAO ? iata2icao(airportIata) : airportIata
      return (
        airport === fromFlight.nextStation ||
        (fromFlight.nextStation === "" &&
          airport === fromFlight.destinationStation)
      )
    })

    if (
      (fromFlight.flightMode === FlightMode.Departure ||
        fromFlight.flightMode === FlightMode.Turnaround) &&
      (toFlight.flightMode === FlightMode.Arrival ||
        toFlight.flightMode === FlightMode.Turnaround) &&
      fromFlightAirportExist
    ) {
      // airport - from next or dest
      const airport =
        fromFlight.nextStation === ""
          ? fromFlight.destinationStation
          : fromFlight.nextStation
      toFlight.airport =
        codeType === CodeType.ICAO ? icao2iata(airport) : airport // airport always IATA
      toFlight.originStation = fromFlight.originStation

      if (toFlight.flightMode === FlightMode.Turnaround) {
        // if next stn present, copy service no and (except for gaba) dest
        let destStn = fromFlight.destinationStation
        if (codeType === CodeType.ICAO) {
          destStn = icao2iata(destStn)
        }
        if (destStn !== toFlight.airport) {
          toFlight.serviceNoArr = fromFlight.serviceNoDep
          if (!isGabaPage) {
            toFlight.destinationStation = fromFlight.destinationStation
          }
        }
      }
    }

    return toFlight
  }

  const hideValidation = (fieldName: string) => {
    return showFieldSection(
      fieldName,
      EditMode.ADD,
      transactionPageGroup,
      versionInfo.handlingApplication !== ""
        ? versionInfo.handlingApplication
        : undefined
    )
  }

  const handleGetInvalidFieldsArr = (
    newFlight: Flight,
    flightMode: FlightMode,
    gcrMode: GCRMode
  ): string[] => {
    const depatureMode = flightMode === FlightMode.Departure
    const arrivalMode =
      flightMode === FlightMode.Arrival && gcrMode === GCRMode.REG
    const arrivalFLT =
      flightMode === FlightMode.Arrival && gcrMode !== GCRMode.REG
    const turnAroundMode = flightMode === FlightMode.Turnaround
    const turnAroundFLT =
      flightMode === FlightMode.Turnaround && gcrMode !== GCRMode.REG
    const requiredRegField = gcrMode !== GCRMode.REG && depatureMode
    const config = getFlightSelectorConfiguration(
      airportSeasons,
      season,
      [newFlight.airport],
      codeType
    )

    const requiredFields = {
      airport: false,
      aircraftRegistration: hideValidation(FieldName.aircraftRegistration)
        ? gcrMode === GCRMode.REG && newFlight.aircraftRegistration === ""
        : false,

      aircraftType: hideValidation(FieldName.aircraftType)
        ? newFlight.aircraftType === ""
        : false,
      operatorText: hideValidation(FieldName.operatorText)
        ? newFlight.operatorText === ""
        : false,
      serviceNoDep: hideValidation(FieldName.serviceNoDep)
        ? (requiredRegField || turnAroundFLT) && newFlight.serviceNoDep === ""
        : false,
      mtow: hideValidation(FieldName.mtow) ? newFlight.mtow === null : false,
      handler: hideValidation(FieldName.handler)
        ? newFlight.handler === ""
        : false,
      endDate: false,
      turnaroundDays:
        transactionPageGroup === PageGroup.HANDLING
          ? newFlight.turnaroundDays === null
          : false,
      numSeats: hideValidation(FieldName.numSeats)
        ? newFlight.numSeats === ""
        : false,
      originStation: hideValidation(FieldName.originStation)
        ? (arrivalFLT || arrivalMode || turnAroundMode) &&
          newFlight.originStation === ""
        : false,
      destinationStation: hideValidation(FieldName.destinationStation)
        ? (depatureMode || turnAroundMode) &&
          newFlight.destinationStation === ""
        : false,
      serviceTypeDep: hideValidation(FieldName.serviceTypeDep)
        ? (depatureMode || turnAroundMode) && newFlight.serviceTypeDep === ""
        : false,
      clearedTimeDep: hideValidation(FieldName.clearedTimeDep)
        ? (depatureMode || turnAroundMode) && newFlight.clearedTimeDep === ""
        : false,
      operatorDep: hideValidation(FieldName.operatorDep)
        ? showOperatorField(
            FieldName.operatorDep,
            flightMode,
            EditMode.ADD,
            gcrMode,
            config.operators
          ) && newFlight.operatorDep === ""
        : false,
      operatorArr: hideValidation(FieldName.operatorArr)
        ? showOperatorField(
            FieldName.operatorArr,
            flightMode,
            EditMode.ADD,
            gcrMode,
            config.operators
          ) && newFlight.operatorArr === ""
        : false,
      serviceNoArr: hideValidation(FieldName.serviceNoArr)
        ? (arrivalFLT || turnAroundFLT) && newFlight.serviceNoArr === ""
        : false,
      serviceTypeArr:
        (flightMode === FlightMode.Arrival || turnAroundMode) &&
        newFlight.serviceTypeArr === "",
      clearedTimeArr:
        (flightMode === FlightMode.Arrival || turnAroundMode) &&
        newFlight.clearedTimeArr === "",
      startDate: hideValidation(FieldName.startDate)
        ? newFlight.startDate === ""
        : false,
      vfr: hideValidation(FieldName.vfr) ? newFlight.vfr === null : false,
      towbar: hideValidation(FieldName.towbar)
        ? newFlight.towbar === null
        : false,
      terminalDep:
        (requiredRegField || turnAroundFLT || depatureMode || turnAroundMode) &&
        config?.supportsTerminalRequest &&
        newFlight.terminalDep === null,
      parkingLocation:
        hideValidation(FieldName.parkingLocation) &&
        config.parkingLocations.length > 0
          ? newFlight.parkingLocation === ""
          : false
    }

    const invalidFieldsArr = Object.keys(requiredFields).filter(
      (requiredField) => {
        return requiredFields[requiredField] !== false
      }
    )

    return invalidFieldsArr
  }

  const handleAddFlight = (flightMode: FlightMode, gcrMode?: GCRMode) => {
    setTouched(null)
    const chosenAirport = airportList.includes(airports[0])
      ? airports[0]
      : airportList[0]
    const updTransactions = _.cloneDeep(transactions)
    const gcrAirports =
      gcrMode === GCRMode.REG && handleGetRegAirports().includes(airports[0])
        ? airports[0]
        : handleGetRegAirports()[0]

    let newFlight: Flight = Utils.getEmptyFlight(pageId)

    newFlight.airport = gcrMode === GCRMode.REG ? gcrAirports : chosenAirport
    newFlight.flightMode = flightMode
    newFlight.requestCode = "N"

    if (date && time && season && !transactions.length) {
      const selectedSeason: Season = seasons.find((s) => s.code === season)
      const dt: Date = Utils.GetValidDateInSeason(
        selectedSeason.startDate,
        selectedSeason.endDate,
        date // string (shortDateStr 25AUG)
      )
      if (dt != null) {
        const propsDate: string = Utils.formatDate(dt)
        newFlight.startDate = propsDate
        newFlight.endDate = propsDate
        newFlight.clearedTimeArr =
          flightMode === FlightMode.Turnaround ||
          flightMode === FlightMode.Arrival
            ? time
            : ""
        newFlight.clearedTimeDep =
          flightMode === FlightMode.Turnaround ||
          flightMode === FlightMode.Departure
            ? time
            : ""
        newFlight.airport = airports[0]
      }
    }

    updTransactions.push({
      transId: uuid(),
      gcrMode: gcrMode ? gcrMode : "",
      flights: [newFlight]
    }) //adding temp transId for CSSTransition - removing before sending to backend
    setTransactions(updTransactions)
    setTransactionInFocus(updTransactions.length - 1)

    setInvalidFieldsArr([
      ...invalidFieldsArr,
      {
        id: updTransactions[updTransactions.length - 1].transId,
        arr: handleGetInvalidFieldsArr(newFlight, flightMode, gcrMode)
      }
    ])
  }

  const handleAddOutAndBack = (flightMode: FlightMode, gcrMode?: GCRMode) => {
    // NOTE: flightMode is always undefined here
    const updTransactions = _.cloneDeep(transactions)

    const chosenAirports = airportList.includes(airports[0])
      ? airports
      : airportList

    const gcrAirports =
      gcrMode === GCRMode.REG && handleGetRegAirports().includes(airports[0])
        ? airports
        : handleGetRegAirports()

    const _airports = gcrMode === GCRMode.REG ? gcrAirports : chosenAirports

    setTouched(null)
    let newDepFlight: Flight = Utils.getEmptyFlight(pageId)
    newDepFlight.airport = _airports[0]
    newDepFlight.flightMode = FlightMode.Departure

    newDepFlight.requestCode = "N"

    let newArrFlight: Flight = Utils.getEmptyFlight(pageId)
    newArrFlight.airport = _airports[0]
    newArrFlight.flightMode = FlightMode.Arrival

    newArrFlight.requestCode = "N"

    updTransactions.push({
      transId: uuid(),
      gcrMode: gcrMode ? gcrMode : "",
      flights: [newDepFlight]
    })

    //adding temp transId for CSSTransition - removing before sending to backend
    updTransactions.push({
      transId: uuid(),
      gcrMode: gcrMode ? gcrMode : "",
      flights: [newArrFlight]
    })

    //adding temp transId for CSSTransition - removing before sending to backend
    setTransactions(updTransactions)
    setTransactionInFocus(updTransactions.length - 2)

    setInvalidFieldsArr([
      ...invalidFieldsArr,
      {
        id: updTransactions[updTransactions.length - 2].transId,
        arr: handleGetInvalidFieldsArr(
          newDepFlight,
          FlightMode.Departure,
          gcrMode
        )
      },
      {
        id: updTransactions[updTransactions.length - 1].transId,
        arr: handleGetInvalidFieldsArr(
          newArrFlight,
          FlightMode.Arrival,
          gcrMode
        )
      }
    ])
  }

  const [values, setValues] = useState({
    startDate: "",
    endDate: "",
    turnaroundDays: 0,
    airport: "",
    woop: 1
  })
  const [error, setError] = useState(false)
  const dateHasError =
    dateRangeIsValid(values.startDate, values.endDate) ||
    values.turnaroundDays < 0 ||
    values.turnaroundDays > 99

  const dateHasErrText =
    values.turnaroundDays < 0
      ? CommonErrorText.departureDateGreater
      : CommonErrorText.nineDaysDateGreater

  const handleUpdTransaction = (
    newTransaction: Transaction,
    transIdx: number,
    error?: boolean,
    field?: string,
    val?: any
  ) => {
    setValues(newTransaction.flights[0])

    const airport = field === "airport" ? val : ""
    const config = getFlightSelectorConfiguration(
      airportSeasons,
      season,
      [airport],
      codeType
    )
    const supportsTerminal = config?.supportsTerminalRequest
    const supportsParkingLocation = config?.parkingLocations.length > 0
    const isAirportField = field === "airport"
    const updTransactions = _.cloneDeep(transactions)
    setTouched(null)
    setError(error)
    const changedTransId = updTransactions[transIdx].transId
    const cardChosen = updTransactions.filter(
      (a) => a.transId === changedTransId
    )
    const flightModeChosen = cardChosen[0].flights[0].flightMode

    const changedFlightM = flightModeChosen
    const changedGcrMode = updTransactions[transIdx].gcrMode
    const currentChangedId = invalidFieldsArr.find(
      (a) => a.id === changedTransId
    )
    // const y = invalidOutAndBackFieldsArr.find((a) => a.id === changedTransId)

    if (field === undefined) {
      setInvalidFieldsArr((lists) =>
        lists.map((list) => {
          const a = list.arr.filter(
            (fieldName) => newTransaction.flights[0][fieldName] === ""
          )
          return list.id === changedTransId ? { ...list, arr: a } : list
        })
      )
    }

    if (!error) {
      if (field === "startDate") {
        setInvalidFieldsArr((lists) =>
          lists.map((list) => {
            const a = list.arr.filter((a) => a !== "endDate")
            return list.id === changedTransId ? { ...list, arr: a } : list
          })
        )
      }

      const terminalDep = "terminalDep"

      const terminalDepValue = newTransaction.flights[0]?.terminalDep

      const depRegFlight =
        changedGcrMode === GCRMode.REG &&
        changedFlightM === FlightMode.Departure

      const arrRegFlight =
        changedGcrMode === GCRMode.REG && changedFlightM === FlightMode.Arrival

      const turnAroundRegMode =
        changedGcrMode === GCRMode.REG &&
        changedFlightM === FlightMode.Turnaround

      const isDepartureFlight =
        changedFlightM === FlightMode.Departure || depRegFlight
      const isArrivalFlight =
        changedFlightM === FlightMode.Arrival || arrRegFlight

      const isTurnAroundFlight =
        changedFlightM === FlightMode.Turnaround || turnAroundRegMode

      if (isAirportField && changedGcrMode === GCRMode.REG) {
        const requiredOperatorArr = showOperatorField(
          FieldName.operatorArr,
          flightModeChosen,
          EditMode.ADD,
          changedGcrMode,
          config.operators
        )
        const requiredOperatorDep = showOperatorField(
          FieldName.operatorDep,
          flightModeChosen,
          EditMode.ADD,
          changedGcrMode,
          config.operators
        )

        if (
          (requiredOperatorArr &&
            !currentChangedId.arr.includes(FieldName.operatorArr)) ||
          (requiredOperatorDep &&
            !currentChangedId.arr.includes(FieldName.operatorDep))
        ) {
          setInvalidFieldsArr((lists) =>
            lists.map((list) => {
              return list.id === changedTransId
                ? {
                    ...list,
                    arr: [
                      ...list.arr,
                      requiredOperatorArr
                        ? FieldName.operatorArr
                        : FieldName.operatorDep
                    ]
                  }
                : list
            })
          )
        }

        if (
          (!requiredOperatorArr &&
            currentChangedId.arr.includes(FieldName.operatorArr)) ||
          (!requiredOperatorDep &&
            currentChangedId.arr.includes(FieldName.operatorDep))
        ) {
          setInvalidFieldsArr((lists) =>
            lists.map((list) => {
              return list.id === changedTransId
                ? {
                    ...list,
                    arr: list.arr.filter(
                      (e) =>
                        e !== FieldName.operatorArr &&
                        e !== FieldName.operatorDep
                    )
                  }
                : list
            })
          )
        }
      }

      if (!supportsTerminal && isAirportField && terminalDepValue) {
        newTransaction.flights[0].terminalDep = null
      }

      if (!supportsTerminal) {
        if (isTurnAroundFlight && isAirportField) {
          setInvalidFieldsArr((lists) =>
            lists.map((list) => {
              const a = list.arr.filter((e) => e !== terminalDep)
              return list.id === changedTransId ? { ...list, arr: a } : list
            })
          )
        }
        if (isDepartureFlight && isAirportField) {
          setInvalidFieldsArr((lists) =>
            lists.map((list) => {
              const a = list.arr.filter((e) => e !== terminalDep)

              return list.id === changedTransId ? { ...list, arr: a } : list
            })
          )
        }
      } else {
        if (isTurnAroundFlight && isAirportField) {
          setInvalidFieldsArr((lists) =>
            lists.map((list) => {
              return list.id === changedTransId
                ? { ...list, arr: [...list.arr, terminalDep] }
                : list
            })
          )
        }

        if (isDepartureFlight && isAirportField) {
          if (currentChangedId && !currentChangedId.arr.includes(field)) {
            setInvalidFieldsArr((lists) =>
              lists.map((list) => {
                return list.id === changedTransId
                  ? { ...list, arr: [...list.arr, terminalDep] }
                  : list
              })
            )
          }
        }
      }

      if (isAirportField) {
        const parkingLocFieldName = "parkingLocation"

        if (
          !supportsParkingLocation &&
          currentChangedId &&
          currentChangedId.arr.includes(parkingLocFieldName)
        ) {
          newTransaction.flights[0].parkingLocation = ""

          setInvalidFieldsArr((lists) =>
            lists.map((list) => {
              const a = list.arr.filter((e) => e !== parkingLocFieldName)
              return list.id === changedTransId ? { ...list, arr: a } : list
            })
          )
        }

        if (
          supportsParkingLocation &&
          currentChangedId &&
          !currentChangedId.arr.includes(parkingLocFieldName)
        ) {
          setInvalidFieldsArr((lists) =>
            lists.map((list) => {
              return list.id === changedTransId
                ? { ...list, arr: [...list.arr, parkingLocFieldName] }
                : list
            })
          )
        }
      }

      setInvalidFieldsArr((lists) => {
        return lists.map((list) => {
          const a = list.arr.filter((a) => a !== field)
          return list.id === changedTransId ? { ...list, arr: a } : list
        })
      })
    } else {
      if (currentChangedId && !currentChangedId.arr.includes(field)) {
        setInvalidFieldsArr((lists) =>
          lists.map((list) => {
            return list.id === changedTransId
              ? { ...list, arr: [...list.arr, field] }
              : list
          })
        )
      }
    }

    updTransactions[transIdx] = newTransaction
    setTransactions(updTransactions)
  }

  const handleDeleteTransaction = (e, flightIdx: number) => {
    setTransactions((t) => t.filter((ti, i) => i !== flightIdx))
    const dataId = e.currentTarget.dataset.id
    setError(false)

    const deletedInvalid = invalidFieldsArr.find((a) => a.id === dataId)
    const b =
      deletedInvalid &&
      invalidFieldsArr.filter((a) => a.id !== deletedInvalid.id)

    if (deletedInvalid) {
      setInvalidFieldsArr(b)
    }
  }

  const handleCheckAvailability = (transactionIdx: number) => {
    setTouched(transactions[transactionIdx].transId)
    const invalidFields = invalidFieldsArr.find(
      (i) => transactions[transactionIdx].transId === i.id
    )
    console.log(invalidFields)

    if (error) {
      dispatch(
        updateUserSettingsSelection({
          field: userSettingsKey.showOptionalFields,
          value: true
        })
      )
    }

    if (
      invalidFields &&
      invalidFields.arr.length === 0 &&
      !dateHasError &&
      !error
    ) {
      setArrMatrixAsHtml(null)
      setDepMatrixAsHtml(null)
      setCheckAvailabilityTransIdx(transactionIdx)
      const flight = transactions[transactionIdx].flights[0]
      handleFlightAvailability("Overview", flight.flightMode, flight)
    }
  }

  const handleFlightAvailability = (
    viewMode: string,
    flightMode: string,
    flight: Flight
  ) => {
    appContext.handleSetIsLoading({
      isLoading: true,
      loadingTxt: LoadingText.fetchingAvailability,
      loadingSubTxt: LoadingText.mayTakeFewMin
    })
    const flightForSubmit = Utils.getFlightForSubmit(flight)
    handleGetFlightAvailability(
      pageId,
      {
        useLocalTime: false, // must be false, otherwise backend throws
        viewMode: viewMode, // Overview, Season, Detail
        displayFlightMode: flightMode, // A, D, T - currently ignored in the backend
        flight: flightForSubmit
      },
      (arrMatrix) => {
        appContext.handleSetIsLoading({ isLoading: false })
        setArrMatrixAsHtml(arrMatrix)
      },
      (depMatrix) => {
        appContext.handleSetIsLoading({ isLoading: false })
        setDepMatrixAsHtml(depMatrix)
      },
      () => {
        appContext.handleSetIsLoading({ isLoading: false, fromFailure: true })
      }
    )
  }

  const handleSaveChangesFromFlightAvailability = (flight: Flight) => {
    const updTransactions = _.cloneDeep(transactions)
    updTransactions[checkAvailabilityTransIdx].flights[0].clearedTimeArr =
      flight.clearedTimeArr
    updTransactions[checkAvailabilityTransIdx].flights[0].clearedTimeDep =
      flight.clearedTimeDep
    setTransactions(updTransactions)
    handleCloseFlightAvailability()
  }

  const handleCloseFlightAvailability = () => {
    setArrMatrixAsHtml(null)
    setDepMatrixAsHtml(null)
    setCheckAvailabilityTransIdx(null)
  }

  const handleItemAutoFill = (transIdx: number) => {
    const updTransaction = _.cloneDeep(transactions[transIdx])
    const updFlight = handleAutoFill(
      transactions[transIdx - 1].flights[0],
      transactions[transIdx].flights[0]
    )

    updTransaction.flights[0] = updFlight
    handleUpdTransaction(updTransaction, transIdx)
  }

  const transItemsIds = transactions.map((t) => "transItem" + t.transId)

  const addFlightItems = transactions.map((t, i) => (
    <CSSTransition
      key={t.transId}
      classNames="transaction-item-add"
      timeout={{ enter: 400, exit: 500 }}
    >
      <TransactionItem
        transaction={t}
        editMode={EditMode.ADD}
        codeType={codeType}
        pageGroup={transactionPageGroup}
        airportSeasons={airportSeasons}
        onDelete={(e) => handleDeleteTransaction(e, i)}
        onAutofill={i > 0 ? () => handleItemAutoFill(i) : null}
        disableAutoFillBtn={error}
        onCheckAvailability={() => handleCheckAvailability(i)}
        onChange={(newTransaction: Transaction, error, field, val) => {
          handleUpdTransaction(newTransaction, i, error, field, val)
        }}
        touched={
          touched !== null
            ? touched === "ALL"
              ? true
              : touched === t.transId
            : false
        }
        showTimeRangeError
        onChangeInFocus={(isInFocus) => {
          return setTransactionInFocus(isInFocus ? i : null)
        }}
        inFocus={transactionInFocus === i}
        transactionItemIDs={transItemsIds}
        dateHasError={dateHasError}
        dateHasErrText={dateHasErrText}
        customShowLabelConfig={{
          terminal: {
            showLabel: true,
            showLabelSection: handleHasFlightsWithTerminal(
              airportSeasons,
              transactions
            )
          },
          parkingLocation: {
            showLabel: true,
            showLabelSection: handleHasFlightsWithParkingLoc(
              airportSeasons,
              transactions
            )
          },
          serviceTypeAcReg: {
            showLabel: true,
            showLabelSection: handleHasFlightsWithServiceTypeAcReg(
              airportSeasons,
              transactions
            )
          }
        }}
      />
    </CSSTransition>
  ))
  const responseTransactions = _.cloneDeep(transactions).map((t) => {
    t.transId = null
    t.flights[0] = Utils.getFlightForSubmit(t.flights[0])
    return t
  })
  const handleDispatchTime = () => {
    handleResetFlights()
    setTransactions([])
    setInvalidFieldsArr([])
    deleteAllTransactions(pageId)
  }

  const handleDispatchSeasons = () => {
    setTransactions([])
    setInvalidFieldsArr([])
    deleteAllTransactions(pageId)
  }
  const addValues = {
    handleTimeChange,
    handleCloseModal,
    local,
    showModal,
    selectedTime,
    timeOptions,
    activeSearch: hasActiveCards,
    handleDispatchTime,
    handleDispatchSeasons,
    showContext: true
  }

  return (
    <GabaContext.Provider value={pageConfig}>
      <ResponsePane
        transactions={responseTransactions}
        pageId={pageId}
        season={season}
        useLocalTime={useLocalTime}
        title="Add Flights"
        airportSeasons={airportSeasons}
        codeType={codeType}
        handleAsBlock={handleAsBlock}
        siText=""
        onClose={() => {
          deleteAllTransactions(pageId)
          setTransactions([])
        }}
        onCancel={() => {}}
        fromEditMode={EditMode.ADD}
      />
      <PageTemplate
        pageTitle={pageTitle}
        pageClassName={"ocs-addflights"}
        pageContentClassName=""
      >
        <div className="page-content-wrapper h-100p">
          <section className="ocs-add-flights-btns d-flex justify-content-between align-items-end">
            <div className="ocs-add-flights-btn d-flex align-items-end flex-1">
              <div className="ocs-mr-12">
                <AddContext.Provider value={addValues}>
                  <SeasonSelector
                    airportSeasons={airportSeasons}
                    supportsICAO={codeType === "ICAO"}
                  />
                </AddContext.Provider>
              </div>
            </div>
            <div className="d-flex ocs-mt-20 ocs-add-flights-btns-container">
              {!isHandlingPage ? (
                <>
                  <CSSTransition
                    classNames="btn-click-indicator"
                    timeout={{ enter: 500, exit: 500 }}
                    in={indicatorClicked}
                    onEntered={() => setIndicatorClicked(false)}
                  >
                    <AddFlightBtn
                      isGabaPage={isGabaPage}
                      icon={"flight_takeoff"}
                      disabled={airportSeasons.length === 0}
                      title={FlightText.departure}
                      content={BtnText.addDeparture}
                      handleAddFlight={handleAddFlight}
                      flightMode={FlightMode.Departure}
                      hideAcReg={hideAcReg}
                    />
                  </CSSTransition>
                  <CSSTransition
                    classNames="btn-click-indicator"
                    timeout={{ enter: 500, exit: 500 }}
                    in={indicatorClicked}
                    onEntered={() => setIndicatorClicked(false)}
                  >
                    <AddFlightBtn
                      isGabaPage={isGabaPage}
                      icon={"flight_land"}
                      disabled={airportSeasons.length === 0}
                      title={FlightText.arrival}
                      content={BtnText.addArrival}
                      handleAddFlight={handleAddFlight}
                      flightMode={FlightMode.Arrival}
                      hideAcReg={hideAcReg}
                    />
                  </CSSTransition>
                  <CSSTransition
                    classNames="btn-click-indicator"
                    timeout={{ enter: 500, exit: 500 }}
                    in={indicatorClicked}
                    onEntered={() => setIndicatorClicked(false)}
                  >
                    <AddFlightBtn
                      isGabaPage={isGabaPage}
                      icon={"flight_land"}
                      doubleIcon="flight_takeoff"
                      doubleIconName="flight_takeoff"
                      disabled={airportSeasons.length === 0}
                      title={FlightText.turnaround}
                      content={BtnText.addTurnAround}
                      handleAddFlight={handleAddFlight}
                      flightMode={FlightMode.Turnaround}
                      hideAcReg={hideAcReg}
                    />
                  </CSSTransition>
                  <CSSTransition
                    classNames="btn-click-indicator"
                    timeout={{ enter: 500, exit: 500 }}
                    in={indicatorClicked}
                    onEntered={() => setIndicatorClicked(false)}
                  >
                    <AddFlightBtn
                      isGabaPage={isGabaPage}
                      icon={"flight_takeoff"}
                      doubleIcon="flight_land"
                      doubleIconName="flight_land"
                      disabled={airportSeasons.length === 0}
                      title={FlightText.outAndBack}
                      content={BtnText.addOutAndBack}
                      handleAddFlight={handleAddOutAndBack}
                      hideAcReg={hideAcReg}
                    />
                  </CSSTransition>
                </>
              ) : (
                <CSSTransition
                  classNames="btn-click-indicator"
                  timeout={{ enter: 500, exit: 500 }}
                  in={indicatorClicked}
                  onEntered={() => setIndicatorClicked(false)}
                >
                  <AddFlightBtn
                    disabled={false}
                    isHandlingPage={isHandlingPage}
                    title={FlightText.turnaround}
                    icon={"flight_land"}
                    doubleIcon="flight_takeoff"
                    doubleIconName="flight_takeoff"
                    content={"Add Flight"}
                    handleAddFlight={handleAddFlight}
                    flightMode={FlightMode.Turnaround}
                  />
                </CSSTransition>
              )}
            </div>

            <div className="d-flex flex-1 justify-content-end mq-flex-direction-row ">
              {!isGabaPage && !isHandlingPage && (
                <SwitchBtn
                  identifier={"handleAsBlock"}
                  name={"handleAsBlock"}
                  label={"Send as Block"}
                  checked={handleAsBlock}
                  callback={(value, _name) => {
                    setHandleAsBlock(value)
                  }}
                />
              )}

              <div
                className={`ocs-add-flights-submit-btn hide-on-desktop ${
                  (isGabaPage || isHandlingPage) && !hideAcReg
                    ? "add-flights-gaba"
                    : ""
                }`}
              >
                <Button
                  disabled={transactions.length === 0}
                  onClick={handleSubmit(startTransaction)}
                  variant={"primary"}
                  type="submit"
                  className="mq-btn-height"
                  idName="sendAllBtn"
                >
                  {FormText.sendAll}
                </Button>
              </div>
            </div>
          </section>

          <SwitchBtn
            identifier={"showOptionalFields"}
            name={"showOptionalFields"}
            label={"Show Required Fields Only"}
            checked={!showOptionalFields}
            className="hide-on-desktop ocs-mb-8 show-required-fields"
            callback={(value, _name) => {
              dispatch(
                updateUserSettingsSelection({
                  field: userSettingsKey.showOptionalFields,
                  value: !showOptionalFields
                })
              )
            }}
          />

          <form
            className="overflow ocs-mt-20 ocs-pb-20 ocs-form add-flights-form"
            style={{ width: "100%", overflowX: "auto" }}
          >
            <div
              style={{
                width: "fit-content",
                minWidth: transactions.length > 0 ? "auto" : "90%",
                marginLeft: "auto",
                marginRight: "auto",
                height: "100%"
              }}
            >
              <div className="ocs-mb-12 hide-on-mobile">
                <SwitchBtn
                  identifier={"showOptionalFields"}
                  name={"showOptionalFields"}
                  label={"Show Required Fields Only"}
                  checked={!showOptionalFields}
                  callback={(value, _name) => {
                    dispatch(
                      updateUserSettingsSelection({
                        field: userSettingsKey.showOptionalFields,
                        value: !showOptionalFields
                      })
                    )
                  }}
                />
              </div>
              <TransitionGroup component={null}>
                {addFlightItems}
              </TransitionGroup>
              <div
                className="ocs-add-transaction-item-indicator hide-on-mobile"
                onClick={() => setIndicatorClicked(true)}
              >
                <p>
                  {"Add " +
                    (addFlightItems.length > 0 ? "another" : "a") +
                    " flight - please use the buttons above"}
                </p>
              </div>
            </div>
            <div
              className={`ocs-add-flights-submit-btn hide-on-mobile ${
                (isGabaPage || isHandlingPage) && !hideAcReg
                  ? "add-flights-gaba"
                  : ""
              }}`}
            >
              <Button
                disabled={transactions.length === 0}
                onClick={handleSubmit(startTransaction)}
                variant={"primary"}
                type="submit"
                className="mq-btn-height"
                idName="sendAllBtn"
              >
                {FormText.sendAll}
              </Button>
            </div>
          </form>
        </div>

        {transactions.length > 0 &&
          checkAvailabilityTransIdx >= 0 &&
          (arrMatrixAsHtml !== null || depMatrixAsHtml !== null) && (
            <div>
              <FlightAvailabilityPanel
                transId={transactions[checkAvailabilityTransIdx].transId}
                grcMode={transactions[checkAvailabilityTransIdx].gcrMode}
                airportSeasons={airportSeasons}
                editMode={EditMode.ADD}
                pageGroup={transactionPageGroup}
                codeType={codeType}
                flight={transactions[checkAvailabilityTransIdx].flights[0]}
                arrMatrix={arrMatrixAsHtml}
                depMatrix={depMatrixAsHtml}
                onChangeAvailability={handleFlightAvailability}
                onSave={handleSaveChangesFromFlightAvailability}
                onCancel={handleCloseFlightAvailability}
                isHandlingPage={isHandlingPage}
              />
            </div>
          )}
      </PageTemplate>
    </GabaContext.Provider>
  )
}
